import React, { Component, PropTypes } from 'react';
import Cropper from 'cropperjs';
import { debounce } from 'lodash/fp';

// share imports
import Button from 'share/components/controls/Izenda.Button.jsx';
import GridLayout from 'share/components/controls/Izenda.GridLayout.jsx';
import Input from 'share/components/controls/Izenda.Input.js';
import localizer from 'share/localization/Izenda.Localizer';

/**
 * @class ReportPartBackgroundImage
 * @description a modal that is used to set and crop a background image for a report part
 */
class ReportPartBackgroundImage extends Component {
    constructor(props) {
        super(props);
        this.imageRef = null;
        this.previewRef = null;
        this.cropper = null;
        this.state = {
            resetZoom: true,
            ...props.value
        };

        this.onCropDebounce = debounce(250, this.setCropValues);
    }
    
    componentDidMount() {
        this.setState({...this.props.value});
        this.initializeCropper();
    };

    componentWillUnmount() {
        if (this.cropper) {
            this.cropper.destroy();
        }
    }

    /**
     * @method initializeCropper
     * @description initialize an instance of Cropper that allows the user to crop an image
     * by zooming and moving it around a specific sized crop box
     */
    initializeCropper() {
        const {
            height,
            width
        } = this.props;
        this.cropper = new Cropper(this.imageRef, {
            aspectRatio:  width() / height(),
            background: false,
            crop: this.onCropDebounce,
            cropBoxMovable: false,
            cropBoxResizable: false,
            dragMode: 'move',
            guides: false,
            minContainerHeight: height(),
            minContainerWidth: width(),
            ready: this.setInitialView,
            toggleDragModeOnDblclick: false
        });
    };

    /**
     * @method setInitialView
     * @description center the crop box in the middle of the cropper, adjust the image relative to
     * the canvasData if it was previously set, reset the zoom if the image is new
     */
    setInitialView = () => {
        const containerData = this.cropper.getContainerData();

        this.cropper.setCropBoxData({
            left: (containerData.width - this.props.width()) / 2,
            top: (containerData.height - this.props.height()) / 2,
            width: this.props.width(),
            height: this.props.height()
        });

        if (this.state.resetZoom) {
            this.cropper.zoomTo(1);
            this.setState({ resetZoom: false });
        }

        if (this.state.canvasData) {
            this.cropper.setCanvasData({
                left: this.state.canvasData.left,
                top: this.state.canvasData.top,
                width: this.state.canvasData.width,
                height: this.state.canvasData.height
            });
        }
        this.setCropValues();
    }

    /**
     * @method getValue
     * @description returns data about background image, dataUrl, and canvas to be set in rp model
     */
    getValue = () => {
        return {
            ...this.state
        };
    }

    /**
     * @method setCropValues
     * @description set the canvasData state values when zoom or crop is finished
     */
    setCropValues = () => {
        const canvasDataUrl = this.cropper.getCroppedCanvas({
            width: this.props.width(),
            height: this.props.height()
        }).toDataURL();
        this.setState({
            dataUrl: canvasDataUrl,
            canvasData: {
                ...this.cropper.getCanvasData()
            }
        });
    }

    /**
     * @method setImageSource
     * @description callback to set user input image URL in state and set url in cropper
     * @param {Object} event input onChange event data
     */
    setImageSource = (event) => {
        this.setState({
            imgSrc: event.target.value,
            canvasData: null,
            resetZoom: true
        });
        this.cropper.replace(event.target.value);
    }

    /**
     * @method onReset
     * @description callback to reset the state and cropper to original values passed in
     */
    onReset = () => {
        this.setState({
            resetZoom: true,
            ...this.props.value
        });
        if (this.cropper) {
            this.cropper.destroy();
        }
        this.initializeCropper();
    }

    /**
     * @method onRemove
     * @description callback to remove background image
     */
    onRemove = () => {
        this.setState({
            canvasData: null,
            dataUrl: '',
            imgSrc: '',
            resetZoom: true
        });
        this.cropper.destroy();
    }

    render() {
        const {
            width,
            height,
            value
        } = this.props;

        return (
            <div className="rp-background-img">
                <GridLayout>
                    <GridLayout.Row>
                        <GridLayout.Col cols="2" className="u_controlHeight">
                            <p>{localizer.getText('PROPERTIES_BUILDER_BACKGROUND_IMAGE_SOURCE')}</p>
                        </GridLayout.Col>
                        <GridLayout.Col cols="8">
                            <Input
                                onChange={this.setImageSource}
                                value={this.state.imgSrc}
                            />
                        </GridLayout.Col>
                        <GridLayout.Col cols="1">
                            <Button
                                disabled={!this.state.imgSrc}
                                label={localizer.getText('COMMON_LABEL_RESET')}
                                onClick={this.onReset}
                            />
                        </GridLayout.Col>
                        <GridLayout.Col cols="1">
                            <Button
                                label={localizer.getText('COMMON_LABEL_REMOVE')}
                                onClick={this.onRemove}
                            />
                        </GridLayout.Col>
                    </GridLayout.Row>
                </GridLayout>
                <div className="image-container">
                    <img
                        alt=""
                        className="image"
                        ref={(ref) => { this.imageRef = ref; }}
                        src={this.state.imgSrc}
                    />
                </div>
            </div>
        );
    }
}

ReportPartBackgroundImage.propTypes = {
    value: PropTypes.shape({
        imgSrc: PropTypes.string,
        dataUrl: PropTypes.string,
        canvasData: PropTypes.object,
        resetZoom: PropTypes.bool
    }),
    weight: PropTypes.func,
    height: PropTypes.func
};

export default ReportPartBackgroundImage;



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/PropertiesBuilder/Izenda.ReportPartBackgroundImage.jsx